// Weight and balance: the loading sheet, the CG, and whether the result is
// inside the aircraft's CG envelope, at take-off and at landing. Ported
// from webplan-modern's computeWeightBalance() and isInsideEnvelope().
//
// Units are the flight manual's: masses in kg, arms in metres aft of the
// datum, fuel in US gallons or litres as loaded. The envelope is a polygon
// of {arm, kg} points (flight.aircraft_performance.cg_envelope) in drawing
// order.
//
// Two webplan behaviours are deliberately NOT ported:
//   * A load exactly on the envelope's edge counted as OUTSIDE (plain ray
//     casting), so an aircraft loaded to exactly MTOW failed. The edge is
//     the limit and is inside.
//   * The landing state was computed with zero fuel burned, so it always
//     equalled take-off. Callers now pass the trip fuel.
//
// Pure; see navigation.ts.

import type { FuelType } from './constants'
import { LITRES_PER_USG } from './fuelPlan'

// kg per litre. webplan kept a separate per-USG table (2.725, 2.839,
// 3.1797) that was these times 3.785 to within rounding; one table avoids
// the two disagreeing.
export const FUEL_DENSITY_KG_PER_LITRE: Record<FuelType, number> = {
  AVGAS: 0.72,
  MOGAS: 0.75,
  DIESEL: 0.84,
}

export const FUEL_UNITS = ['usg', 'l'] as const
export type FuelUnit = (typeof FUEL_UNITS)[number]

export function fuelKg(amount: number, unit: FuelUnit, fuelType: FuelType): number {
  const litres = unit === 'usg' ? amount * LITRES_PER_USG : amount
  return litres * FUEL_DENSITY_KG_PER_LITRE[fuelType]
}

export interface EnvelopePoint {
  arm: number
  kg: number
}

/** One baggage / cargo compartment, as the flight manual lists it. */
export interface BaggageArea {
  label: string
  /** Metres aft of the datum, to the middle of the compartment. */
  arm: number
  maxKg: number
}

export interface WeightBalanceAircraft {
  fuelType: FuelType
  /** flight.aircraft.mtow */
  mtowKg: number | null
  emptyWeightKg: number
  armBasicMass: number
  armFrontPax: number
  armRearPax: number
  armCargo: number
  armFuelMain: number
  armFuelRes: number
  cgEnvelope: EnvelopePoint[]
  /** Empty or absent: one cargo station at `armCargo` with no limit. */
  baggageAreas?: BaggageArea[]
  /** The areas together may not hold more than this; null / absent: no joint limit. */
  baggageMaxTotalKg?: number | null
  /** false: a two-seater, the rear row is not there. Absent: true. */
  hasRearSeats?: boolean
  /** Usable fuel the tanks hold, US gallons; 0 / absent: not known. */
  maxFuelMainUsg?: number
  maxFuelResUsg?: number
}

export interface WeightBalanceLoad {
  frontKg: number
  rearKg: number
  cargoKg: number
  /** Per baggage area, in the order of `baggageAreas`; wins over `cargoKg` when the aircraft has areas. */
  baggageKg?: number[]
  fuelMain: number
  fuelRes: number
  fuelUnit: FuelUnit
}

export const LOADING_STATIONS = ['empty', 'front', 'rear', 'cargo', 'fuelMain', 'fuelRes'] as const
export type LoadingStation = (typeof LOADING_STATIONS)[number]

export interface LoadingRow {
  station: LoadingStation
  kg: number
  arm: number
  moment: number
}

export interface MassAndCg {
  kg: number
  moment: number
  /** null when there is no mass at all. */
  cgArm: number | null
  withinEnvelope: boolean
  withinMtow: boolean
}

export interface WeightBalance {
  rows: LoadingRow[]
  /** Everything loaded, fuelled, engine not yet started. */
  ramp: MassAndCg
  /** The ramp state less the taxi fuel. Equals `ramp` when no taxi fuel is given. */
  takeoff: MassAndCg
  /** After the trip fuel has come out of the main tank as well. */
  landing: MassAndCg
  /** Take-off and landing both inside the envelope and at or under MTOW. */
  ok: boolean
}

function state(kg: number, moment: number, aircraft: WeightBalanceAircraft): MassAndCg {
  const cgArm = kg > 0 ? moment / kg : null
  return {
    kg,
    moment,
    cgArm,
    withinEnvelope: cgArm !== null && isInsideEnvelope({ arm: cgArm, kg }, aircraft.cgEnvelope),
    withinMtow: aircraft.mtowKg === null || kg <= aircraft.mtowKg + EDGE_TOLERANCE,
  }
}

/** US gallons burned taxiing for `minutes` at `litresPer10Min`. */
export function taxiFuelUsg(litresPer10Min: number, minutes: number): number {
  return (Math.max(litresPer10Min, 0) * Math.max(minutes, 0)) / 10 / LITRES_PER_USG
}

/**
 * The baggage the sheet loads: the sum over the areas and the arm that gives
 * the same moment as the areas do one by one. Without areas, `cargoKg` at
 * `armCargo`.
 */
export function cargoStation(aircraft: WeightBalanceAircraft, load: WeightBalanceLoad): { kg: number; arm: number } {
  const areas = aircraft.baggageAreas ?? []
  if (areas.length === 0) return { kg: load.cargoKg, arm: aircraft.armCargo }
  const kgs = areas.map((_, i) => Math.max(load.baggageKg?.[i] ?? 0, 0))
  const kg = kgs.reduce((sum, v) => sum + v, 0)
  if (kg === 0) return { kg: 0, arm: areas[0]!.arm }
  return { kg, arm: kgs.reduce((sum, v, i) => sum + v * areas[i]!.arm, 0) / kg }
}

/**
 * `load` as this aircraft can carry it: nothing on a rear row it does not
 * have, and baggage only in the compartments it has. A plan keeps the last
 * figures entered, so changing from a four-seater to a Cessna 152 must not
 * carry the old rear row along.
 */
export function loadForAircraft(aircraft: WeightBalanceAircraft, load: WeightBalanceLoad): WeightBalanceLoad {
  const areas = aircraft.baggageAreas ?? []
  const baggageKg = areas.map((_, i) => load.baggageKg?.[i] ?? 0)
  return {
    ...load,
    rearKg: aircraft.hasRearSeats === false ? 0 : load.rearKg,
    ...(areas.length > 0 ? { baggageKg, cargoKg: baggageKg.reduce((sum, v) => sum + v, 0) } : { baggageKg: [] }),
  }
}

export type LoadWarning =
  | { code: 'rearSeats'; kg: number }
  | { code: 'baggageArea'; index: number; label: string; kg: number; maxKg: number }
  | { code: 'baggageTotal'; kg: number; maxKg: number }
  | { code: 'fuelMain' | 'fuelRes'; usg: number; maxUsg: number }

/**
 * Every loading limit the load exceeds: a baggage area, the areas together,
 * a tank, or weight on a rear seat the aircraft does not have. The mass and
 * CG limits are the sheet's own `withinMtow` and `withinEnvelope`.
 */
export function loadWarnings(aircraft: WeightBalanceAircraft, load: WeightBalanceLoad): LoadWarning[] {
  const warnings: LoadWarning[] = []
  if (aircraft.hasRearSeats === false && load.rearKg > 0) warnings.push({ code: 'rearSeats', kg: load.rearKg })
  const areas = aircraft.baggageAreas ?? []
  areas.forEach((area, index) => {
    const kg = load.baggageKg?.[index] ?? 0
    if (kg > area.maxKg + EDGE_TOLERANCE) warnings.push({ code: 'baggageArea', index, label: area.label, kg, maxKg: area.maxKg })
  })
  const total = cargoStation(aircraft, load).kg
  const joint = aircraft.baggageMaxTotalKg ?? null
  if (areas.length > 0 && joint !== null && total > joint + EDGE_TOLERANCE) {
    warnings.push({ code: 'baggageTotal', kg: total, maxKg: joint })
  }
  const toUsg = (amount: number) => (load.fuelUnit === 'usg' ? amount : amount / LITRES_PER_USG)
  for (const [code, amount, max] of [
    ['fuelMain', load.fuelMain, aircraft.maxFuelMainUsg ?? 0],
    ['fuelRes', load.fuelRes, aircraft.maxFuelResUsg ?? 0],
  ] as const) {
    const usg = toUsg(amount)
    // A tank of 0 is "not known" (the column's default), not "holds nothing".
    if (max > 0 && usg > max + 0.05) warnings.push({ code, usg, maxUsg: max })
  }
  return warnings
}

/** US gallons burned in `minutes` at `flowUsgPerHour`. */
export function fuelBurnUsg(flowUsgPerHour: number, minutes: number): number {
  return (Math.max(flowUsgPerHour, 0) * Math.max(minutes, 0)) / 60
}

/**
 * The loading sheet for `load`. `taxiUsg` US gallons come out of the main
 * tank between ramp and take-off, then `burnUsg` more by landing (neither
 * ever more than the main tank held).
 */
export function computeWeightBalance(
  aircraft: WeightBalanceAircraft,
  requested: WeightBalanceLoad,
  burnUsg = 0,
  taxiUsg = 0,
): WeightBalance {
  const load = loadForAircraft(aircraft, requested)
  const fuelMainKg = fuelKg(load.fuelMain, load.fuelUnit, aircraft.fuelType)
  const fuelResKg = fuelKg(load.fuelRes, load.fuelUnit, aircraft.fuelType)
  const cargo = cargoStation(aircraft, load)
  const rows: LoadingRow[] = (
    [
      ['empty', aircraft.emptyWeightKg, aircraft.armBasicMass],
      ['front', load.frontKg, aircraft.armFrontPax],
      ['rear', load.rearKg, aircraft.armRearPax],
      ['cargo', cargo.kg, cargo.arm],
      ['fuelMain', fuelMainKg, aircraft.armFuelMain],
      ['fuelRes', fuelResKg, aircraft.armFuelRes],
    ] as const
  ).map(([station, kg, arm]) => ({ station, kg, arm, moment: kg * arm }))

  const kg = rows.reduce((sum, row) => sum + row.kg, 0)
  const moment = rows.reduce((sum, row) => sum + row.moment, 0)
  const taxiKg = Math.min(fuelKg(Math.max(taxiUsg, 0), 'usg', aircraft.fuelType), fuelMainKg)
  const burnKg = Math.min(fuelKg(Math.max(burnUsg, 0), 'usg', aircraft.fuelType), fuelMainKg - taxiKg)

  const ramp = state(kg, moment, aircraft)
  const takeoff = state(kg - taxiKg, moment - taxiKg * aircraft.armFuelMain, aircraft)
  const landing = state(kg - taxiKg - burnKg, moment - (taxiKg + burnKg) * aircraft.armFuelMain, aircraft)
  return {
    rows,
    ramp,
    takeoff,
    landing,
    ok: takeoff.withinEnvelope && takeoff.withinMtow && landing.withinEnvelope && landing.withinMtow,
  }
}

// Figures are entered to a few decimals; a point this close to an edge is
// on it.
const EDGE_TOLERANCE = 1e-6

function onSegment(p: EnvelopePoint, a: EnvelopePoint, b: EnvelopePoint): boolean {
  const cross = (p.arm - a.arm) * (b.kg - a.kg) - (p.kg - a.kg) * (b.arm - a.arm)
  const length = Math.hypot(b.arm - a.arm, b.kg - a.kg)
  if (length === 0) return Math.hypot(p.arm - a.arm, p.kg - a.kg) <= EDGE_TOLERANCE
  if (Math.abs(cross) / length > EDGE_TOLERANCE) return false
  const dot = (p.arm - a.arm) * (b.arm - a.arm) + (p.kg - a.kg) * (b.kg - a.kg)
  return dot >= -EDGE_TOLERANCE && dot <= length * length + EDGE_TOLERANCE
}

/** Inside the polygon or on its edge. Fewer than three points: nothing is inside. */
export function isInsideEnvelope(point: EnvelopePoint, envelope: EnvelopePoint[]): boolean {
  if (envelope.length < 3) return false
  let inside = false
  for (let i = 0, j = envelope.length - 1; i < envelope.length; j = i++) {
    const a = envelope[i]!
    const b = envelope[j]!
    if (onSegment(point, a, b)) return true
    if (a.kg > point.kg !== b.kg > point.kg && point.arm < ((b.arm - a.arm) * (point.kg - a.kg)) / (b.kg - a.kg) + a.arm) {
      inside = !inside
    }
  }
  return inside
}

export interface EnvelopeRange {
  minArm: number
  maxArm: number
  minKg: number
  maxKg: number
}

/** The chart's axes: the envelope's own extremes (webplan's chart_* columns). */
export function envelopeRange(envelope: EnvelopePoint[]): EnvelopeRange | null {
  if (envelope.length === 0) return null
  const arms = envelope.map((p) => p.arm)
  const masses = envelope.map((p) => p.kg)
  return {
    minArm: Math.min(...arms),
    maxArm: Math.max(...arms),
    minKg: Math.min(...masses),
    maxKg: Math.max(...masses),
  }
}

/**
 * The forward and aft CG limits at `kg`: where a horizontal line at that
 * mass cuts the envelope. null when the mass is above or below it.
 */
export function envelopeLimitsAtMass(
  envelope: EnvelopePoint[],
  kg: number,
): { forward: number; aft: number } | null {
  const arms: number[] = []
  for (let i = 0, j = envelope.length - 1; i < envelope.length; j = i++) {
    const a = envelope[i]!
    const b = envelope[j]!
    if (a.kg === b.kg) {
      if (Math.abs(a.kg - kg) <= EDGE_TOLERANCE) arms.push(a.arm, b.arm)
      continue
    }
    const t = (kg - a.kg) / (b.kg - a.kg)
    if (t >= -EDGE_TOLERANCE && t <= 1 + EDGE_TOLERANCE) arms.push(a.arm + t * (b.arm - a.arm))
  }
  if (arms.length === 0) return null
  return { forward: Math.min(...arms), aft: Math.max(...arms) }
}

export interface WorkedFuel {
  station: 'fuelMain' | 'fuelRes'
  amount: number
  unit: FuelUnit
  litres: number
  /** kg per litre. */
  density: number
  kg: number
}

export interface WorkedState extends MassAndCg {
  /** Forward and aft CG limits at this mass; null outside the envelope's mass range. */
  limits: { forward: number; aft: number } | null
  /** MTOW less this mass; null when the aircraft has no MTOW on file. */
  mtowMarginKg: number | null
}

/** A quantity of fuel in every unit a pilot might want it in. */
export interface FuelQuantity {
  usg: number
  litres: number
  kg: number
}

export interface WorkedBurn extends FuelQuantity {
  minutes: number
  /** kg x the main tank's arm: what comes off the moment. */
  moment: number
}

export interface WorkedAssumptions {
  flowUsgPerHour: number
  flightMinutes: number
  taxiMinutes: number
  taxiLitresPerTenMin: number
  /** kg per litre. */
  density: number
  litresPerUsg: number
}

/** The masses from empty aircraft to landing, in the order they build up. */
export interface WeightLadder {
  basicEmptyKg: number
  payloadKg: number
  zeroFuelKg: number
  fuelLoaded: FuelQuantity
  rampKg: number
  taxi: WorkedBurn
  takeoffKg: number
  trip: WorkedBurn
  landingKg: number
  /** Still in the tanks on landing. */
  landingFuel: FuelQuantity
}

export interface WorkedWeightBalance extends WeightBalance {
  /** Each tank from the amount loaded to kg, the step the loading sheet skips. */
  fuel: WorkedFuel[]
  assumptions: WorkedAssumptions
  ladder: WeightLadder
  ramp: WorkedState
  takeoff: WorkedState
  landing: WorkedState
}

export interface WorkedOptions {
  /** Fuel flow, US gallons per hour. */
  flowUsgPerHour: number
  /** Time in the air, minutes. */
  flightMinutes: number
  /** Time from ramp to take-off, minutes. */
  taxiMinutes: number
  /** Fuel burned per 10 minutes of taxi, litres. */
  taxiLitresPerTenMin: number
}

const quantity = (kg: number, density: number): FuelQuantity => ({
  kg,
  litres: kg / density,
  usg: kg / density / LITRES_PER_USG,
})

/**
 * computeWeightBalance with every intermediate figure kept, so a pilot can
 * redo the sum by hand: fuel volume to mass, mass x arm = moment, the sums,
 * CG = moment / mass, the envelope's limits at that mass, and the fuel
 * burned from ramp to take-off and from take-off to landing. The figures are
 * the same ones; nothing here is computed a second way.
 */
export function workedWeightBalance(
  aircraft: WeightBalanceAircraft,
  requested: WeightBalanceLoad,
  options: WorkedOptions,
): WorkedWeightBalance {
  const load = loadForAircraft(aircraft, requested)
  const taxiUsg = taxiFuelUsg(options.taxiLitresPerTenMin, options.taxiMinutes)
  const tripUsg = fuelBurnUsg(options.flowUsgPerHour, options.flightMinutes)
  const wb = computeWeightBalance(aircraft, load, tripUsg, taxiUsg)
  const density = FUEL_DENSITY_KG_PER_LITRE[aircraft.fuelType]
  const tank = (station: WorkedFuel['station'], amount: number): WorkedFuel => ({
    station,
    amount,
    unit: load.fuelUnit,
    litres: load.fuelUnit === 'usg' ? amount * LITRES_PER_USG : amount,
    density,
    kg: fuelKg(amount, load.fuelUnit, aircraft.fuelType),
  })
  const fuel = [tank('fuelMain', load.fuelMain), tank('fuelRes', load.fuelRes)]

  const worked = (s: MassAndCg): WorkedState => ({
    ...s,
    limits: envelopeLimitsAtMass(aircraft.cgEnvelope, s.kg),
    mtowMarginKg: aircraft.mtowKg === null ? null : aircraft.mtowKg - s.kg,
  })

  const burn = (kg: number, minutes: number): WorkedBurn => ({
    ...quantity(kg, density),
    minutes,
    moment: kg * aircraft.armFuelMain,
  })
  const taxiKg = wb.ramp.kg - wb.takeoff.kg
  const tripKg = wb.takeoff.kg - wb.landing.kg
  const fuelLoadedKg = fuel[0]!.kg + fuel[1]!.kg
  const payloadKg = load.frontKg + load.rearKg + load.cargoKg
  const ladder: WeightLadder = {
    basicEmptyKg: aircraft.emptyWeightKg,
    payloadKg,
    zeroFuelKg: wb.ramp.kg - fuelLoadedKg,
    fuelLoaded: quantity(fuelLoadedKg, density),
    rampKg: wb.ramp.kg,
    taxi: burn(taxiKg, options.taxiMinutes),
    takeoffKg: wb.takeoff.kg,
    trip: burn(tripKg, options.flightMinutes),
    landingKg: wb.landing.kg,
    landingFuel: quantity(fuelLoadedKg - taxiKg - tripKg, density),
  }
  return {
    ...wb,
    fuel,
    assumptions: {
      flowUsgPerHour: options.flowUsgPerHour,
      flightMinutes: options.flightMinutes,
      taxiMinutes: options.taxiMinutes,
      taxiLitresPerTenMin: options.taxiLitresPerTenMin,
      density,
      litresPerUsg: LITRES_PER_USG,
    },
    ladder,
    ramp: worked(wb.ramp),
    takeoff: worked(wb.takeoff),
    landing: worked(wb.landing),
  }
}
